import React, {
	PropsWithChildren,
	ReactNode,
	createContext,
	useContext,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';
import { useResizeDetector } from 'react-resize-detector';

type Bounds = { x: number; y: number; width: number; height: number };
type Direction = 'north' | 'south';
type Children = (direction: Direction) => ReactNode;

const Context = createContext(
	(undefined as unknown) as {
		updatePopover: (id: string, bounds: Bounds, children: Children) => void;
		hidePopover: (id: string) => void;
	},
);

function PopoverContainer(props: { bounds: Bounds; children: Children }) {
	const { height, ref } = useResizeDetector();

	const minY = props.bounds.y - (height ?? 0) - window.scrollY;

	const south = minY < 0;

	return (
		<div
			ref={ref}
			style={{
				position: 'absolute',
				left: props.bounds.x + props.bounds.width / 2,
				top: props.bounds.y + (south ? props.bounds.height : 0),
				transform: south ? 'translateX(-50%)' : 'translateX(-50%) translateY(-100%)',
				zIndex: 6,
			}}
		>
			{props.children(south ? 'south' : 'north')}
		</div>
	);
}

export default function PopoverProvider(props: PropsWithChildren<{}>) {
	const [popovers, setPopovers] = useState(
		new Map<
			string,
			{
				bounds: Bounds;
				children: Children;
			}
		>(),
	);

	const updatePopover = useRef(function updatePopover(
		id: string,
		bounds: Bounds,
		children: Children,
	) {
		setPopovers((popovers) => {
			popovers = new Map(popovers);
			popovers.set(id, { bounds, children });
			return popovers;
		});
	}).current;

	const hidePopover = useRef(function hidePopover(id: string) {
		setPopovers((popovers) => {
			popovers = new Map(popovers);
			popovers.delete(id);
			return popovers;
		});
	}).current;

	const value = useRef({ updatePopover, hidePopover }).current;

	return (
		<Context.Provider value={value}>
			{props.children}
			{[...popovers.entries()].map(([id, { bounds, children }]) => (
				<PopoverContainer key={id} bounds={bounds}>
					{children}
				</PopoverContainer>
			))}
		</Context.Provider>
	);
}

function position(element?: HTMLElement): { x: number; y: number } {
	if (!element) {
		return { x: 0, y: 0 };
	}

	const { x, y } = position(element.offsetParent as HTMLElement);

	const transform = new DOMMatrix(element.style.transform);

	return {
		x: x + element.offsetLeft + transform.e,
		y: y + element.offsetTop - (element.offsetParent ? element.scrollTop : 0) + transform.f,
	};
}

export function PopoverPortal(props: { children: (direction: Direction) => ReactNode }) {
	const context = useContext(Context);
	const id = useRef(Math.random().toString()).current;
	const ref = useRef<HTMLDivElement>(null);

	useLayoutEffect(() => {
		if (!ref.current) {
			return;
		}

		context.updatePopover(
			id,
			{
				...position(ref.current),
				width: ref.current.offsetWidth,
				height: ref.current.offsetHeight,
			},
			props.children,
		);
	}, [!!ref, props.children]);

	useLayoutEffect(() => {
		return () => context.hidePopover(id);
	}, []);

	return (
		<div
			ref={ref}
			style={{
				position: 'absolute',
				width: '100%',
				height: '100%',
				pointerEvents: 'none',
			}}
		/>
	);
}
